// https://developers.facebook.com/docs/facebook-pixel/advanced/

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: Window['fbq'];
  }
}

export interface PurchaseParams {
  value: number;
  currency: string;
  content_ids?: string[];
  content_name?: string;
  content_type?: string;
  product_line?: string;
  tier?: string;
  transaction_id?: string;
}

export function initFbPixel(pixelId: string): void {
  if (typeof window === 'undefined') return;
  if (window.fbq) return;

  /* eslint-disable */
  const n: any = (window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  } as any);
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  /* eslint-enable */

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(script, first);

  window.fbq!('init', pixelId);
}

/**
 * `eventID` pairs a browser event with the same event sent server-side through the
 * Conversions API, so Meta counts it once.
 * https://developers.facebook.com/docs/marketing-api/conversions-api/deduplicate-pixel-and-server-events
 */
export interface FbEventOptions {
  eventID?: string;
}

export function trackFbEvent(event: 'PageView'): void;
export function trackFbEvent(event: 'Purchase', params: PurchaseParams, options?: FbEventOptions): void;
export function trackFbEvent(
  event: 'CompleteRegistration',
  params?: Record<string, unknown>,
  options?: FbEventOptions
): void;
export function trackFbEvent(
  event: 'ProductCustomized',
  params?: Record<string, unknown>,
  options?: FbEventOptions
): void;
export function trackFbEvent(
  event: 'InitiateCheckout',
  params?: Record<string, unknown>,
  options?: FbEventOptions
): void;
export function trackFbEvent(event: 'AddToCart', params?: Record<string, unknown>, options?: FbEventOptions): void;
export function trackFbEvent(event: 'Lead', params?: Record<string, unknown>, options?: FbEventOptions): void;
export function trackFbEvent(event: 'Search', params?: Record<string, unknown>, options?: FbEventOptions): void;
export function trackFbEvent(event: AccessEvent, params?: Record<string, unknown>, options?: FbEventOptions): void;
export function trackFbEvent(
  event: string,
  params?: Record<string, unknown> | PurchaseParams,
  options?: FbEventOptions
): void {
  if (typeof window === 'undefined' || !window.fbq) {
    console.log(`[fb-pixel] stub: ${event}`, params ?? '');
    return;
  }

  if (options?.eventID) {
    window.fbq('track', event, params ?? {}, { eventID: options.eventID });
  } else if (params) {
    window.fbq('track', event, params);
  } else {
    window.fbq('track', event);
  }
}

export function voteSearchDedupKey(userId: string, targetType: string, targetId: string): string {
  return `fb_search_community_vote_${userId}_${targetType}_${targetId}`;
}

/** Fallback when sessionStorage read/write fails (private mode, quota). */
const voteSearchDedupMemory = new Set<string>();

function isVoteSearchDeduped(key: string): boolean {
  if (voteSearchDedupMemory.has(key)) return true;

  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key)) return true;
  } catch {
    /* private mode / quota */
  }

  return false;
}

function markVoteSearchDeduped(key: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, 'true');
      return;
    }
  } catch {
    /* private mode / quota */
  }

  voteSearchDedupMemory.add(key);
}

/** Fire Meta Search for community up-votes once per user per voted item (sessionStorage dedup). */
export function trackVoteSearchOnce(userId: string, targetType: string, targetId: string): void {
  if (typeof window === 'undefined') return;

  const key = voteSearchDedupKey(userId, targetType, targetId);
  if (isVoteSearchDeduped(key)) return;

  // Claim the dedup slot before firing so a failed setItem cannot double-count.
  markVoteSearchDeduped(key);
  trackFbEvent('Search', { content_category: 'community_vote' });
}

export type UploadActivationEvent = 'ProductCustomized' | 'AddToCart';

/**
 * Dedup key shared by the browser pixel and the API's Conversions API send for the
 * same upload. Keyed on the image: both sides know it, and each flag is stamped on
 * exactly one image per user.
 */
export function uploadActivationEventId(event: UploadActivationEvent, imageId: string | number): string {
  return `${event === 'AddToCart' ? 'tier2' : 'first-upload'}-${imageId}`;
}

export interface UploadActivationFlags {
  id: string | number;
  isFirstUpload?: boolean | null;
  isTier2Activated?: boolean | null;
}

/** GTM dataLayer event names for the activation milestones (GA4 / Google Ads read these via GTM). */
export const UPLOAD_ACTIVATION_DATALAYER_EVENTS = {
  ProductCustomized: 'first_upload',
  AddToCart: 'tier2_activation',
} as const satisfies Record<UploadActivationEvent, string>;

function pushUploadActivationToDataLayer(event: UploadActivationEvent, image_id: string): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { dataLayer?: object[] };
  w.dataLayer = w.dataLayer ?? [];
  w.dataLayer.push({ event: UPLOAD_ACTIVATION_DATALAYER_EVENTS[event], image_id });
}

/**
 * Fire the activation events for a `createImage` result — the Meta pixel event plus a GTM
 * dataLayer event so GA4 sees the same milestone. The API stamps each flag once per user,
 * so this fires at most once per user per event — no client dedup needed.
 * - `ProductCustomized` / `first_upload` = first artwork upload.
 * - `AddToCart` / `tier2_activation` = Tier 2 activation (2nd distinct upload day within 14 days of the first).
 */
export function trackUploadActivation(created: UploadActivationFlags): void {
  const image_id = String(created.id);
  if (created.isFirstUpload) {
    trackFbEvent(
      'ProductCustomized',
      { image_id },
      { eventID: uploadActivationEventId('ProductCustomized', image_id) }
    );
    pushUploadActivationToDataLayer('ProductCustomized', image_id);
  }
  if (created.isTier2Activated) {
    trackFbEvent('AddToCart', { image_id }, { eventID: uploadActivationEventId('AddToCart', image_id) });
    pushUploadActivationToDataLayer('AddToCart', image_id);
  }
}

/**
 * The acquisition-strategy conversions (waitlist → waiting wizard → let in), one per member each:
 * - `Contact` = joined the waitlist.
 * - `Schedule` = finished the waiting wizard while still on the waitlist.
 * - `AddToWishlist` = the same, from our target audience (`isTargetArtist`).
 * - `StartTrial` = let in off the waitlist (a referral unlock, an admin, or the release date).
 *
 * Distinct from `MemberEvent`'s `StartTrial` (day-1 return): that id is `start-trial-<asfId>`,
 * this one is `let-in-<asfId>`.
 */
export type AccessEvent = 'Contact' | 'Schedule' | 'AddToWishlist' | 'StartTrial';

const ACCESS_EVENT_PREFIX: Record<AccessEvent, string> = {
  Contact: 'waitlist',
  Schedule: 'waiting-done',
  AddToWishlist: 'waiting-done-target',
  StartTrial: 'let-in',
};

/** Dedup key shared by the browser pixel and the API's Conversions API send for one member's event. */
export function accessEventId(event: AccessEvent, userId: string | number): string {
  return `${ACCESS_EVENT_PREFIX[event]}-${userId}`;
}

/** Fallback when localStorage read/write fails (private mode, quota). */
const accessFiredMemory = new Set<string>();

/**
 * Fire one access conversion from the browser, once per member per browser. Meta also drops a
 * browser event whose id the server already sent, so a second browser is harmless too.
 */
export function trackAccessEventOnce(event: AccessEvent, userId: string | number): void {
  if (typeof window === 'undefined') return;
  const eventID = accessEventId(event, userId);
  const key = `fb_access_${eventID}`;
  if (accessFiredMemory.has(key)) return;
  accessFiredMemory.add(key);
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, 'true');
  } catch {
    /* private mode / quota — the in-memory set still holds it for this page */
  }
  trackFbEvent(event, { content_category: 'acquisition' }, { eventID });
}

/** Events the API also sends through the Conversions API, outside the upload pair. */
export type MemberEvent = 'InitiateCheckout' | 'Lead' | 'Search' | 'StartTrial' | 'Purchase';

const MEMBER_EVENT_PREFIX: Record<MemberEvent, string> = {
  InitiateCheckout: 'onboarding-complete',
  Lead: 'post',
  Search: 'comment',
  StartTrial: 'start-trial',
  Purchase: 'purchase',
};

/**
 * Dedup key shared by the browser pixel and the API's Conversions API send. `key` is what
 * both sides know: the asfId (`InitiateCheckout`, `StartTrial` — once per member), the
 * post or comment id (`Lead`, `Search`), or the Stripe Checkout session id (`Purchase`).
 */
export function memberEventId(event: MemberEvent, key: string | number): string {
  return `${MEMBER_EVENT_PREFIX[event]}-${key}`;
}

const PURCHASE_SESSION_KEY = 'fb_purchase_checkout_session';

/** A remembered session past this is an abandoned checkout, not the one being returned from. */
export const CHECKOUT_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Remember the Checkout session a member is sent to, so the Purchase fired on their
 * return carries the same `eventID` as the API's copy. localStorage, not sessionStorage:
 * checkout can open in a new tab.
 */
export function rememberCheckoutSession(sessionId: string | null | undefined, now: number = Date.now()): void {
  if (!sessionId) return;
  try {
    localStorage.setItem(PURCHASE_SESSION_KEY, JSON.stringify({ sessionId, startedAt: now }));
  } catch {
    /* private mode / quota */
  }
}

/** Drop the remembered session, e.g. once its return has been handled without a Purchase. */
export function forgetCheckoutSession(): void {
  try {
    localStorage.removeItem(PURCHASE_SESSION_KEY);
  } catch {
    /* private mode */
  }
}

/** The remembered Checkout session, left in place so the caller can still consume it. Stale or malformed entries are dropped. */
export function readCheckoutSessionId(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(PURCHASE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { sessionId?: unknown; startedAt?: unknown };
    const fresh =
      typeof parsed?.sessionId === 'string' &&
      parsed.sessionId.length > 0 &&
      typeof parsed.startedAt === 'number' &&
      now - parsed.startedAt <= CHECKOUT_SESSION_TTL_MS;
    if (fresh) return parsed.sessionId as string;
  } catch {
    /* unreadable storage or entry */
  }
  forgetCheckoutSession();
  return null;
}

/** Purchase options for the remembered session, consumed so a later purchase cannot reuse it. */
export function takePurchaseEventOptions(now: number = Date.now()): FbEventOptions {
  const sessionId = readCheckoutSessionId(now);
  forgetCheckoutSession();
  return sessionId ? { eventID: memberEventId('Purchase', sessionId) } : {};
}
